import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, XCircle, FileText, Clock, CreditCard, Cpu } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const SumupRequestForm = ({ devisId, sumupEmail, setSumupEmail, sumupTel, setSumupTel, sumupLoading, sumupRequested, handleRequestSumupLink, t }) => (
  <div className="card-dark p-4 border border-blue-500/20 max-w-md mx-auto">
    <div className="flex items-center gap-3 mb-3">
      <div className="p-2 rounded-lg bg-blue-500/20">
        <CreditCard className="w-5 h-5 text-blue-400" />
      </div>
      <div>
        <h3 className="font-semibold text-white text-sm">Demande de paiement par lien SumUp</h3>
        <p className="text-[11px] text-gray-400">Remplissez vos coordonnees pour recevoir un lien de paiement</p>
      </div>
    </div>
    {sumupRequested ? (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
        <CheckCircle className="w-4 h-4 text-green-400" />
        <span className="text-sm text-green-400">{t('sumup.requestSent')}</span>
      </div>
    ) : (
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">{t('common.email') || 'Email'} *</label>
          <input type="email" value={sumupEmail} onChange={e => setSumupEmail(e.target.value)}
            placeholder="votre@email.com"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:outline-none"
            data-testid="sumup-client-email" />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">{t('common.phone') || 'Telephone'}</label>
          <input type="tel" value={sumupTel} onChange={e => setSumupTel(e.target.value)}
            placeholder="0694 XX XX XX"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:outline-none"
            data-testid="sumup-client-tel" />
        </div>
        <button onClick={handleRequestSumupLink}
          disabled={sumupLoading || (!sumupEmail && !sumupTel)}
          className="w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="request-sumup-link-btn">
          {sumupLoading ? <div className="loading-spinner w-5 h-5"></div> : <><CreditCard className="w-4 h-4" /> {t('sumup.requestLink')}</>}
        </button>
      </div>
    )}
  </div>
);

const DevisAcceptPage = () => {
  const { devisId } = useParams();
  const { t, tError } = useLanguage();
  const [devis, setDevis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [refused, setRefused] = useState(false);
  const [result, setResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [sumupRequested, setSumupRequested] = useState(false);
  const [sumupLoading, setSumupLoading] = useState(false);
  const [sumupEmail, setSumupEmail] = useState('');
  const [sumupTel, setSumupTel] = useState('');

  useEffect(() => {
    const fetchDevis = async () => {
      try {
        const res = await axios.get(`${API}/api/devis/public/${devisId}`);
        setDevis(res.data);
      } catch (err) {
        setError(tError(err?.response?.data?.detail) || t('devisAccept.notFound'));
      } finally {
        setLoading(false);
      }
    };
    fetchDevis();
  }, [devisId]);

  const handleAccept = async () => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/api/devis/accept/${devisId}`);
      setResult(res.data);
      setAccepted(true);
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('devisAccept.error'));
    } finally {
      setProcessing(false);
    }
  };

  const handleRefuse = async () => {
    if (!window.confirm(t('devisAccept.confirmRefuse'))) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/api/devis/refuse/${devisId}`);
      setRefused(true);
    } catch (err) {
      alert(tError(err?.response?.data?.detail) || t('devisAccept.error'));
    } finally {
      setProcessing(false);
    }
  };

  const handleRequestSumupLink = async () => {
    if (!sumupEmail && !sumupTel) {
      alert('Veuillez saisir votre adresse email ou votre numéro de téléphone.');
      return;
    }
    setSumupLoading(true);
    try {
      const res = await axios.post(`${API}/api/devis/request-sumup-link/${devisId}`, {
        client_email: sumupEmail,
        client_tel: sumupTel,
      });
      setSumupRequested(true);
      alert(res.data.message || 'Demande envoyée !');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.detail;
      if (err?.response?.data?.status === 'already_requested') {
        setSumupRequested(true);
        alert(msg || 'Demande déjà en cours.');
      } else {
        alert(tError(msg) || 'Erreur lors de la demande');
      }
    } finally {
      setSumupLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">{t('devisAccept.error')}</h2>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (refused) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">{t('devisAccept.refused')}</h2>
          <p className="text-gray-400">{t('devisAccept.refusedMsg')} {devis?.ref}. {t('devisAccept.thanks')}</p>
        </div>
      </div>
    );
  }

  if (accepted && result) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4" data-testid="devis-accepted-page">
        <div className="max-w-lg w-full space-y-6">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white">{t('devisAccept.accepted')}</h2>
            <p className="text-gray-400 mt-2">{t('devisAccept.acceptedMsg')} <strong className="text-violet-400">{devis?.ref}</strong></p>
          </div>

          <div className="card-dark p-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">{t('devisAccept.bonCommande')}</span>
              <span className="font-bold text-green-400">{result.bon_commande_ref}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">{t('devisAccept.facture')}</span>
              <span className="font-bold text-red-400">{result.facture_ref}</span>
            </div>
            <p className="text-sm text-gray-400">
              {t('devisAccept.emailSent')}
            </p>
          </div>

          <div className="card-dark p-5 border border-amber-500/30">
            <h3 className="font-bold text-amber-400 mb-3 flex items-center gap-2">
              <CreditCard className="w-5 h-5" /> {t('devisAccept.proceedPayment')}
            </h3>
            <p className="text-sm text-gray-400 mb-4">{t('devisAccept.paymentMsg')}</p>
            {devis?.disable_online_payment ? (
              <SumupRequestForm devisId={devisId} sumupEmail={sumupEmail} setSumupEmail={setSumupEmail} sumupTel={sumupTel} setSumupTel={setSumupTel} sumupLoading={sumupLoading} sumupRequested={sumupRequested} handleRequestSumupLink={handleRequestSumupLink} t={t} />
            ) : (
              <div className="flex gap-3 flex-wrap">
                <a href={`/devis/paiement/${devisId}?mode=acompte`}
                  className="flex-1 py-3 px-4 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 font-semibold text-center transition-colors"
                  data-testid="pay-acompte-btn">
                  {t('devisAccept.deposit50')}<br /><span className="text-lg">{(devis?.total / 2).toFixed(2)} €</span>
                </a>
                <a href={`/devis/paiement/${devisId}?mode=complet`}
                  className="flex-1 py-3 px-4 rounded-lg bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 font-semibold text-center transition-colors"
                  data-testid="pay-complet-btn">
                  {t('devisAccept.fullPayment')}<br /><span className="text-lg">{devis?.total?.toFixed(2)} €</span>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Already handled devis
  if (devis?.status !== 'en_attente') {
    const statusMessages = {
      accepte: { icon: CheckCircle, color: 'text-green-400', msg: t('devisAccept.alreadyAccepted') },
      refuse: { icon: XCircle, color: 'text-red-400', msg: t('devisAccept.alreadyRefused') },
    };
    const st = statusMessages[devis.status] || { icon: Clock, color: 'text-yellow-400', msg: `${t('devisAccept.statusMsg')} : ${devis.status}` };
    const Icon = st.icon;
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <Icon className={`w-16 h-16 ${st.color} mx-auto mb-4`} />
          <h2 className="text-xl font-bold text-white mb-2">Devis {devis.ref}</h2>
          <p className="text-gray-400">{st.msg}</p>
          {devis.status === 'accepte' && (
            <div className="mt-4 space-y-3">
              {!devis.disable_online_payment && (
                <div className="flex gap-3 justify-center flex-wrap">
                  <a href={`/devis/paiement/${devisId}?mode=acompte`}
                    className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 font-semibold text-sm">
                    {t('devisAccept.payDeposit')}
                  </a>
                  <a href={`/devis/paiement/${devisId}?mode=complet`}
                    className="px-4 py-2 rounded-lg bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 font-semibold text-sm">
                    {t('devisAccept.fullPayment')}
                  </a>
                </div>
              )}
              {(devis.disable_online_payment || devis.accept_online_payment) && (
                <SumupRequestForm devisId={devisId} sumupEmail={sumupEmail} setSumupEmail={setSumupEmail} sumupTel={sumupTel} setSumupTel={setSumupTel} sumupLoading={sumupLoading} sumupRequested={sumupRequested} handleRequestSumupLink={handleRequestSumupLink} t={t} />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4" data-testid="devis-accept-page">
      <div className="max-w-lg w-full space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center mb-4">
            <Cpu className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">R.E.Mobiles</h1>
          <p className="text-gray-400 text-sm">{t('devisAccept.homeRepairQuote')}</p>
        </div>

        {/* Devis details */}
        <div className="card-dark p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-violet-400 flex items-center gap-2">
              <FileText className="w-5 h-5" /> Devis {devis.ref}
            </h2>
            <span className="text-xs text-gray-500">{new Date(devis.created_at).toLocaleDateString('fr-FR')}</span>
          </div>

          <div className="text-sm text-gray-400">
            <p>{t('devisAccept.hello')} <strong className="text-white">{devis.client_nom}</strong>,</p>
            <p className="mt-1">{t('devisAccept.findBelow')}</p>
          </div>

          {/* Items table */}
          <div className="rounded-lg border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-violet-500/10">
                  <th className="text-left p-3 text-violet-300 font-medium">{t('devisAccept.designation')}</th>
                  <th className="text-center p-3 text-violet-300 font-medium w-12">{t('common.quantity')}</th>
                  <th className="text-right p-3 text-violet-300 font-medium w-24">{t('common.price')}</th>
                  <th className="text-right p-3 text-violet-300 font-medium w-24">{t('common.total')}</th>
                </tr>
              </thead>
              <tbody>
                {devis.lignes?.map((l, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="p-3 text-gray-300">{l.designation}</td>
                    <td className="p-3 text-center text-gray-300">{l.quantite}</td>
                    <td className="p-3 text-right text-gray-300">{parseFloat(l.prix).toFixed(2)} €</td>
                    <td className="p-3 text-right text-white font-medium">{(parseFloat(l.prix) * parseInt(l.quantite)).toFixed(2)} €</td>
                  </tr>
                ))}
                {devis.inclure_deplacement && (
                  <tr className="border-t border-white/5">
                    <td className="p-3 text-gray-300">{t('devisAccept.travelFee')}</td>
                    <td className="p-3 text-center text-gray-300">1</td>
                    <td className="p-3 text-right text-gray-300">{devis.frais_deplacement?.toFixed(2)} €</td>
                    <td className="p-3 text-right text-white font-medium">{devis.frais_deplacement?.toFixed(2)} €</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-violet-500/30 bg-violet-500/5">
                  <td colSpan={3} className="p-3 text-right font-bold text-white">TOTAL</td>
                  <td className="p-3 text-right font-black text-xl text-violet-400">{devis.total?.toFixed(2)} €</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="text-[11px] text-gray-600">{t('devisAccept.tvaNote')}</p>
        </div>

        {/* Accept/Refuse buttons */}
        <div className="flex gap-3">
          <button onClick={handleRefuse} disabled={processing}
            className="flex-1 py-3 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="refuse-devis-btn">
            <XCircle className="w-5 h-5" /> {t('devisAccept.refuse')}
          </button>
          <button onClick={handleAccept} disabled={processing}
            className="flex-1 py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="accept-devis-btn">
            {processing ? <div className="loading-spinner w-5 h-5"></div> : <><CheckCircle className="w-5 h-5" /> {t('devisAccept.accept')}</>}
          </button>
        </div>

        {/* SumUp payment link request - shown when online payment disabled OR when accept_online_payment is true on the devis */}
        {(devis?.disable_online_payment || devis?.accept_online_payment) && (
          <SumupRequestForm devisId={devisId} sumupEmail={sumupEmail} setSumupEmail={setSumupEmail} sumupTel={sumupTel} setSumupTel={setSumupTel} sumupLoading={sumupLoading} sumupRequested={sumupRequested} handleRequestSumupLink={handleRequestSumupLink} t={t} />
        )}

        <p className="text-center text-xs text-gray-600">
          R.E.Mobiles - 102 Avenue General de Gaulle, Porte 2, 97300 Cayenne
        </p>
      </div>
    </div>
  );
};

export default DevisAcceptPage;
